import {useCallback, useEffect, useMemo, useRef} from "react";
import {
    FloorplanReservationItemDTO
} from "crqlar-react-common/dist/models/floorplan/floorplanReservationItem.dto";
import {isToday} from "crqlar-react-common/dist/utils/date.util";
import {useTimeframe} from "./useTimeframe";

import {useFloorplanContext} from "./useFloorplanContext";

interface Options {
    active: FloorplanReservationItemDTO[];
    activeTab: number;
}

export function useSidebarScroll({active, activeTab}: Options) {
    const {
        date,
        referenceTime,
        timeframeStart,
        reservationDaySlice,
    } = useTimeframe();
    const {restaurantId} = useFloorplanContext();

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollPositions = useRef<Record<number, number>>({});
    const scrollTargetElRef = useRef<HTMLDivElement | null>(null);
    const lastAutoScrollKeyRef = useRef<string>();
    const autoScrollPendingRef = useRef(false);
    const previousTabRef = useRef(activeTab);

    const selectedDateIsToday = isToday(date);
    const referenceTimeMs = referenceTime.getTime();
    const timeframeStartMs = timeframeStart?.getTime();
    const autoScrollKey = selectedDateIsToday
        ? [
            restaurantId,
            date.getTime(),
            reservationDaySlice,
            reservationDaySlice === "now" ? "now" : timeframeStartMs,
        ].join(":")
        : undefined;

    const targetIndex = useMemo(() => {
        if (!selectedDateIsToday) return -1;

        const ongoingIdx = active.findIndex((r) => {
            const start = new Date(r.restaurantReservation?.expectedStartDate ?? 0);
            const end = new Date(r.restaurantReservation?.expectedEndDate ?? 0);
            return referenceTimeMs >= start.getTime() && referenceTimeMs < end.getTime();
        });
        if (ongoingIdx !== -1) return ongoingIdx;
        return active.findIndex((r) =>
            new Date(r.restaurantReservation?.expectedStartDate ?? 0).getTime() > referenceTimeMs
        );
    }, [active, referenceTimeMs, selectedDateIsToday]);

    const scrollTo = useCallback((el: HTMLDivElement, auto = false) => {
        const container = scrollContainerRef.current;
        if (!container) return;
        if (auto) autoScrollPendingRef.current = true;

        requestAnimationFrame(() => {
            container.scrollTop = el.offsetTop - container.offsetTop;
            if (auto) autoScrollPendingRef.current = false;
        });
    }, []);

    const autoScrollTo = useCallback((el: HTMLDivElement | null) => {
        if (
            !el
            || activeTab !== 0
            || !autoScrollKey
            || lastAutoScrollKeyRef.current === autoScrollKey
        ) return;

        lastAutoScrollKeyRef.current = autoScrollKey;
        scrollTo(el, true);
    }, [activeTab, autoScrollKey, scrollTo]);

    const setScrollTargetRef = useCallback((el: HTMLDivElement | null) => {
        scrollTargetElRef.current = el;
        autoScrollTo(el);
    }, [autoScrollTo]);

    // Auto-scroll once for today's selected restaurant/timeframe. A data refresh
    // keeps the same key and therefore cannot move a user's manual scroll position.
    useEffect(() => {
        if (!autoScrollKey) {
            lastAutoScrollKeyRef.current = undefined;
            return;
        }

        const el = scrollTargetElRef.current;
        autoScrollTo(el);
    }, [autoScrollKey, autoScrollTo]);

    // Restore saved scroll position when switching tabs
    useEffect(() => {
        const previousTab = previousTabRef.current;
        previousTabRef.current = activeTab;
        if (previousTab === activeTab || autoScrollPendingRef.current) return;

        const container = scrollContainerRef.current;
        if (!container) return;
        requestAnimationFrame(() => {
            container.scrollTop = scrollPositions.current[activeTab] ?? 0;
        });
    }, [activeTab]);

    const saveScrollPosition = (tab: number) => {
        if (scrollContainerRef.current) {
            scrollPositions.current[tab] = scrollContainerRef.current.scrollTop;
        }
    };

    return {
        scrollContainerRef,
        setScrollTargetRef,
        saveScrollPosition,
        targetIndex,
    };
}
